import React from "react";
import styled from "styled-components";
import axios from "axios";
import Image from "next/image";
import Head from "next/head";
import { CourseJsonLd } from "next-seo";
import Player from "@vimeo/player";
import { FaPlayCircle, FaCheckCircle } from "react-icons/fa";
import * as Router from "next/router";
import ReactMarkdown from "react-markdown";

import config from "../../../config.client";
import firebase from "../../../firebase.client";
import * as u from "../../../utils";
import * as gtag from "../../../utils/gtag";
import * as h from "../../../utils/hooks";
import Button from "../../../components/Button";
import * as T from "../../../components/Typography";
import * as I from "../../../components/Icons";
import * as UserContext from "../../../components/UserContext";
import Link from "../../../components/Link";
import WhatsYourMind from "../../../components/WhatsYourMind";
import ActivityPost from "../../../components/ActivityPost";
import UpsellModal from "../../../components/UpsellModal";
import RxLink from "@/components/ui/inputs/RxLink";

export default function Course(props) {
  const userInfo = React.useContext(UserContext.Context);
  const { hasLoaded } = userInfo;
  const router = Router.useRouter();
  const playerRef = React.useRef(null);
  const playerContainerRef = React.useRef(null);
  const [myCourses, setMyCourses] = React.useState([]);
  const [activityIndex, setActivityIndex] = React.useState(0);
  const [modalType, setModalType] = React.useState(false);
  const currentActivity = props.lessonActivitiesCollection.items[activityIndex];
  const isLoggedIn = !!userInfo.id;
  const canAccessCourse = props.isFree || (isLoggedIn && userInfo.isPaid);

  React.useEffect(() => {
    destroyPlayer();
    createPlayer();
  }, [activityIndex, showVideo]);
  React.useEffect(() => {
    getMyCourses();
  }, [userInfo]);
  React.useEffect(() => {
    initActivityProgress();
  }, [myCourses, activityIndex]);

  const formatA = (props) => <TT.A href={props.href}>{props.children}</TT.A>;
  const formatP = (props) => <TT.CLA>{props.children}</TT.CLA>;
  const components = { a: formatA, p: formatP };

  return (
    <S.Container>
      {getHead()}
      <CourseJsonLd {...getJsonLdProps()} />
      {hasLoaded && (
        <>
          <UpsellModal type={modalType} setType={setModalType} />
          <S.BackWrapper>
            <S.BackButton onClick={() => router.back()}>
              <I.ArrowLeft colour={u.colours.charcoal} />
            </S.BackButton>
            <div>
              <TT.CXLA weight="700">{props.lessonName}</TT.CXLA>
              <VideoInfo />
            </div>
          </S.BackWrapper>
          <S.Grid>
            <div>
              {currentActivity?.image?.url && (
                <S.HeroImageContainer>
                  <S.BgImage
                    src={currentActivity?.image?.url}
                    layout="fill"
                    objectFit="cover"
                    unoptimized
                  />
                  <Image
                    src={currentActivity?.image?.url}
                    layout="fill"
                    objectFit="contain"
                    alt=""
                  />
                </S.HeroImageContainer>
              )}
              {currentActivity?.vimeoUrl && showVideo()}
              {currentActivity?.chapterPdf?.url && (
                <S.WorkbookButton
                  href={currentActivity?.chapterPdf?.url}
                  target="_blank"
                >
                  <S.ImageContainer>
                    <Image
                      className="image"
                      src="/images/accelerate/workbooks-icon.png"
                      layout="fill"
                      objectFit="cover"
                      alt=""
                    />
                  </S.ImageContainer>
                  <S.WorkbookText>
                    <T.CXL color={u.colours.shell}>
                      {currentActivity?.activityTitle}
                    </T.CXL>
                    <S.LinkText className="link-text" weight={600}>
                      Download pdf
                      <S.Arrow className="arrow" />
                    </S.LinkText>
                  </S.WorkbookText>
                </S.WorkbookButton>
              )}
              <S.IntroWrapper>
                <TT.CXLB>{props.lessonName}</TT.CXLB>
                {currentActivity.chapterCopy ? (
                  <>
                    <ReactMarkdown components={components}>
                      {currentActivity.chapterCopy}
                    </ReactMarkdown>
                  </>
                ) : (
                  <JsonToText
                    json={currentActivity?.activityIntroduction?.json}
                  />
                )}
              </S.IntroWrapper>
            </div>
            <Activities />
            <CourseInfo />
          </S.Grid>
          <Comments />
        </>
      )}
    </S.Container>
  );

  function showVideo() {
    const isLoggedIn = !!userInfo.id;

    if (canAccessCourse) {
      return <S.Video ref={playerContainerRef} title={props.lessonName} />;
    }

    if (!isLoggedIn) {
      return (
        <Placeholder {...props}>
          <TT.CLC>Sign up or log in to watch our courses.</TT.CLC>
          <Button
            onClick={() => {
              setModalType("watch");
            }}
          >
            Sign up <S.Lock />
          </Button>
        </Placeholder>
      );
    }

    return (
      <Placeholder {...props}>
        <TT.CLC>
          Upgrade to AllBright Digital membership to access this course.
        </TT.CLC>
        <Link href={"/digital/select-plan"}>
          <Button>
            Upgrade to unlock <S.Lock />
          </Button>
        </Link>
      </Placeholder>
    );

    function Placeholder(props) {
      return (
        <S.ImageWrapper>
          {props?.coverImage?.url && (
            <Image
              src={props.coverImage.url}
              alt={props.coverImage.title}
              layout="fill"
              objectFit="cover"
            />
          )}
          <S.Overlay>{props.children}</S.Overlay>
        </S.ImageWrapper>
      );
    }
  }

  async function getMyCourses() {
    if (!userInfo.id) return;
    const data = (
      await firebase.firestore().collection("users").doc(userInfo.id).get()
    ).data();
    if (data?.courses?.length) {
      setMyCourses(data.courses);
      loadIncompleteActivity(data.courses);
    }
  }

  function loadIncompleteActivity(myCoursesData) {
    const currentCourse = myCoursesData.find(
      (course) => course.id === props.sys.id
    );
    const completedActivityCount = currentCourse?.completedLessons.length || 0;
    const activityCount = props.lessonActivitiesCollection.items.length;
    setActivityIndex(completedActivityCount % activityCount);
  }

  function destroyPlayer() {
    if (!playerRef.current) return;
    playerRef.current.destroy();
  }

  function createPlayer() {
    if (!playerContainerRef.current) return;

    playerRef.current = new Player(playerContainerRef.current, {
      url: currentActivity?.vimeoUrl,
      width: playerContainerRef.current?.clientWidth,
      autoplay: false,
    });
  }

  function initActivityProgress() {
    if (!userInfo.id || !playerRef.current) return;

    setTimeout(() => {
      playerRef.current.off("ended");
      playerRef.current.on("ended", saveCourseActivity);
      playerRef.current.on("ended", loadNextActivity);
    }, 100);
  }

  async function saveCourseActivity() {
    const myCoursesUpdate = [...myCourses];
    const currentActivityId = currentActivity.sys.id;
    const hasCourse = myCoursesUpdate
      .map((course) => course.id)
      .includes(props.sys.id);
    const activities =
      hasCourse &&
      myCoursesUpdate.find((course) => course.id === props.sys.id)
        .completedLessons;
    const hasActivity = hasCourse && activities.includes(currentActivityId);

    if (hasActivity) return;
    if (hasCourse && !hasActivity) activities.push(currentActivityId);
    if (!hasCourse) {
      myCoursesUpdate.push({
        id: props.sys.id,
        completedLessons: [currentActivityId],
      });
      gtag.event({
        name: gtag.GA_EVENT.ACADEMY_CHAPTER,
        category: "Academy",
        label: "Complete Chapter",
        value: `First from ${props.lessonName}`,
      });
    } else {
      gtag.event({
        name: gtag.GA_EVENT.ACADEMY_CHAPTER,
        category: "Academy",
        label: "Complete Chapter",
        value: `Continued from ${props.lessonName}`,
      });
    }

    const userRef = firebase.firestore().collection("users").doc(userInfo.id);
    await userRef.set({ courses: myCoursesUpdate }, { merge: true });
    setMyCourses(myCoursesUpdate);
  }

  function loadNextActivity() {
    if (props.lessonActivitiesCollection.items.length > activityIndex + 1) {
      setActivityIndex(activityIndex + 1);
    } else if (
      props.lessonActivitiesCollection.items.length ===
      activityIndex + 1
    ) {
      gtag.event({
        name: gtag.GA_EVENT.ACADEMY_CHAPTER,
        category: "Academy",
        label: "Complete Chapter",
        value: `Last from ${props.lessonName}`,
      });
    }
  }

  function VideoInfo() {
    const videoCount = props.lessonActivitiesCollection.items.length;
    const videoDuration = props.lessonActivitiesCollection.items.reduce(
      (acc, activity) => (acc += activity.runtime),
      0
    );
    const nearlyMinutes = u.secToNearestMin(videoDuration);
    const pluralise = (n) => (n > 1 ? "s" : "");
    return (
      <TT.CLB
        weight={500}
      >{`${videoCount} chapter${pluralise(videoCount)} | ${nearlyMinutes} minute${pluralise(nearlyMinutes)}`}</TT.CLB>
    );
  }

  function Activities() {
    const isComplete = (course) => course.id === props.sys.id;
    const completedActivities = myCourses.find(isComplete)?.completedLessons;

    return (
      <S.ActivitiesWrapper>
        <S.Activities>
          {props.lessonActivitiesCollection.items.map(
            getActivity(completedActivities)
          )}
        </S.Activities>
        {canAccessCourse && props.courseWorksheet && (
          <S.A href={props.courseWorksheet?.url} target="_blank" download>
            <Button>
              <S.Download />
              Download course worksheet
            </Button>
          </S.A>
        )}
      </S.ActivitiesWrapper>
    );
  }

  function getActivity(completedActivities) {
    return (activity, index) => {
      const complete = completedActivities?.includes(activity.sys.id);

      return (
        <S.Activity
          key={index}
          onClick={() => setActivityIndex(index)}
          active={index === activityIndex}
          complete={complete}
          title={activity.activityTitle}
        >
          <S.Flex>
            {complete ? <S.FaCheckCircle /> : <S.FaPlayCircle />}
            <TT.CM>{activity.activityTitle}</TT.CM>
          </S.Flex>
          <T.CM>{u.secToMin(activity.runtime)}</T.CM>
        </S.Activity>
      );
    };
  }

  function CourseInfo() {
    const [tab, setTab] = React.useState("intro");

    if (!canAccessCourse) return null;

    return (
      <SI.Container>
        <SI.TabsWrapper>
          <SI.Tabs>{getTabs()}</SI.Tabs>
        </SI.TabsWrapper>
        {renderContent()}
      </SI.Container>
    );

    function getTabs() {
      return (
        <>
          {props.lessonIntroductionText && (
            <SI.TextWrapper>
              <SI.A isActive={tab === "intro"} onClick={() => setTab("intro")}>
                Introduction
              </SI.A>
            </SI.TextWrapper>
          )}
          {props.authorBiography && (
            <SI.TextWrapper>
              <SI.A isActive={tab === "bio"} onClick={() => setTab("bio")}>
                Author Bio
              </SI.A>
            </SI.TextWrapper>
          )}
          {props.additionalDownloadableResourcesCollection.items.length > 0 && (
            <SI.TextWrapper>
              <SI.A
                isActive={tab === "resources"}
                onClick={() => setTab("resources")}
              >
                Additional Resources
              </SI.A>
            </SI.TextWrapper>
          )}
          {props.readingListUse && (
            <SI.TextWrapper>
              <SI.A
                isActive={tab === "reading"}
                onClick={() => setTab("reading")}
              >
                Reading List
              </SI.A>
            </SI.TextWrapper>
          )}
        </>
      );
    }

    function renderContent() {
      if (tab === "bio")
        return <JsonToText json={props.authorBiography?.json} />;
      if (tab === "resources")
        return props.additionalDownloadableResourcesCollection.items.map(
          (resource) => <Resource key={resource.url} {...resource} />
        );

      const formatA = (props) => (
        <TT.A href={props.href}>{props.children}</TT.A>
      );
      const formatP = (props) => <TT.CLA>{props.children}</TT.CLA>;
      const components = { a: formatA, p: formatP };
      if (tab === "intro")
        return (
          <ReactMarkdown components={components}>
            {props.lessonIntroductionText}
          </ReactMarkdown>
        );
      if (tab === "reading")
        return (
          <ReactMarkdown components={components}>
            {props.readingListUse}
          </ReactMarkdown>
        );
    }
  }

  function JsonToText(props) {
    if (!props.json) return null;
    return props.json.content.map(getContent);

    function getContent(content, index) {
      if (content.nodeType === "text") return getText(content, index);
      if (content.nodeType === "paragraph")
        return <TT.CLA key={index}>{content.content.map(getContent)}</TT.CLA>;
      if (content.nodeType === "heading-3")
        return <S.H3 key={index}>{content.content.map(getContent)}</S.H3>;
      if (content.nodeType === "hyperlink") {
        return (
          <Link key={index} href={content.data.uri}>
            {content.content.map(getContent)}
          </Link>
        );
      }
      return null;
    }

    function getText(content, index) {
      const marks = content.marks.map((elm) => elm.type);
      if (marks.includes("bold"))
        return (
          <span style={{ fontWeight: "bold" }} key={index}>
            {content.value}
          </span>
        );
      return <span key={index}>{content.value}</span>;
    }
  }

  function Resource(props) {
    return (
      <>
        <T.A href={props.url} target="_blank">
          {props.title}
        </T.A>
        <TT.CLA>{props.description}</TT.CLA>
      </>
    );
  }

  function Comments() {
    const { store, onCreatePost } = h.useStreamFeed("public", props.sys.id);
    const comments = store || props.comments;

    return (
      <S.Comments>
        <T.H3>Comments</T.H3>
        <div onClick={() => !userInfo.id && setModalType("comment")}>
          <WhatsYourMind
            onCreatePost={onCreatePost}
            location="Academy"
            placeholder="What do you think?"
          />
        </div>
        {comments.results?.map(getComment)}
      </S.Comments>
    );

    function getComment(elm) {
      const postProps = {
        key: elm.id,
        type: "comment",
        link: router.asPath,
        pageId: props.sys.id,
        ...elm,
      };

      if (userInfo.id) return <ActivityPost {...postProps} />;

      return (
        <div key={elm.id} onClick={() => setModalType("comment")}>
          <ActivityPost {...elm} />
        </div>
      );
    }
  }

  function getHead() {
    const title = `${u.trimStr(props.lessonName, 28)} ${activityIndex + 1} / ${props.lessonActivitiesCollection.items.length} | Academy | AllBright`;
    const description =
      props.lessonActivitiesCollection?.items[0]?.activityIntroduction?.json
        ?.content[0]?.content[0]?.value || "";
    const info = {
      title,
      description,
      imgW: 650,
      imgH: 650,
      imgUrl: props.coverImage?.url,
      imgAlt: props.coverImage?.title,
      url: config.ALLBRIGHT_URL + router.asPath,
    };

    const metaTags = [
      ...u.generateMetaTags(info),
      ...u.generateMetaType("course"),
    ];

    return (
      <Head>
        <title>{title}</title>
        {metaTags.map((elm) => (
          <meta {...elm} key={elm.name || elm.itemprop || elm.property} />
        ))}
      </Head>
    );
  }

  function getJsonLdProps() {
    return {
      courseName: props.lessonName,
      providerName: props.lessonAuthor,
      providerUrl: `${config.ALLBRIGHT_URL}${router.asPath}`,
      description:
        props.lessonActivitiesCollection?.items[0]?.activityIntroduction?.json
          ?.content[0]?.content[0]?.value || "",
    };
  }
}

export async function getStaticPaths() {
  return { paths: [], fallback: "blocking" };
}

export async function getStaticProps({ params }) {
  const config = require("../../../config");

  try {
    const lessonIdData = await axios.post(
      config.CONTENTFUL_GENERAL,
      getLessonIdQuery(params.course)
    );
    const id = lessonIdData.data.data.lessonCollection.items[0].sys.id;
    const {
      data: {
        data: { lesson },
      },
    } = await axios.post(config.CONTENTFUL_GENERAL, getLessonQuery(id));
    const comments = await getPageComments(id);
    return { props: { ...lesson, comments }, revalidate: 3600 };
  } catch (err) {
    console.log(params);
    console.error("✘ academy course error: ", err);
    return { notFound: true };
  }

  async function getPageComments(id) {
    const stream = require("getstream");
    const currClient = await stream.connect(
      config.STREAM_PUBLIC,
      config.STREAM_SECRET,
      config.STREAM_APP_ID
    );
    const baseOpts = { limit: 30, reactions: { recent: true, counts: true } };
    return await currClient?.feed("public", id).get(baseOpts);
  }

  function getLessonIdQuery(course) {
    return {
      query: `{
        lessonCollection(where: { urlSlug: "${course}" }) {
          items { sys { id } }
        }
      }`,
    };
  }

  function getLessonQuery(id) {
    return {
      query: `{
        lesson(id: "${id}") {
          lessonName
          lessonIntroductionText
          courseTags
          categoryTags
          isFree
          lessonAuthor
          sys { id }
          coverImage { title url }
          lessonAuthorAvatar { url }
          authorBiography { json }
          lessonActivitiesCollection {
            items {
              activityTitle
              vimeoUrl
              image { url }
              chapterPdf { url }
              chapterCopy
              runtime
              urlSlug
              audio { url }
              sys { id }
              activityIntroduction { json }
            }
          }
          courseWorksheet {
            url
          }
          readingListUse
          additionalDownloadableResourcesCollection {
            items {
              title
              description
              url
            }
          }
        }
      }`,
    };
  }
}

const S = {};

S.Container = styled.div`
  max-width: 1020px;
  margin: 0 auto;
  padding: 20px 20px 80px;
`;

S.BackWrapper = styled.div`
  align-items: center;
  display: inline-flex;
  margin: 16px 0 16px 16px;

  @media ${u.device.desktop} {
    margin: 20px 0;
  }
`;

S.BackButton = styled.div`
  background-color: ${u.colours.shellGuideline};
  border-radius: 50%;
  display: flex;
  margin-right: 20px;
  padding: 5px;
  cursor: pointer;

  :hover {
    filter: brightness(0.9);
  }
`;

S.Course = styled.div`
  background: white;
`;

S.Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  margin-bottom: 20px;

  @media ${u.device.desktop} {
    grid-template-columns: 1fr 313px;
  }
`;

S.H3Mobile = styled.h3`
  margin-bottom: 20px;
  text-align: center;
  display: block;

  @media ${u.device.tablet} {
    margin-bottom: 30px;
  }

  @media ${u.device.desktop} {
    display: none;
  }
`;

S.Video = styled.div`
  border-radius: 3px 3px 0 0;
  position: relative;
  padding-bottom: 56.25%;
  height: 0;
  margin-bottom: 20px;
  overflow: hidden;

  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
`;

S.ImageWrapper = styled.div`
  position: relative;
  flex: 1;
  margin: 0 0 10px 0;
  padding-top: 55%;

  @media ${u.device.tablet} {
    margin: 0 20px 20px 0;
  }
`;

S.WorkbookButton = styled(RxLink)`
  margin: 0 0 20px 0;
  border-radius: 5px;
  padding: 15px 20px;
  display: flex;
  align-items: center;
  gap: 30px;
  background: linear-gradient(
    45deg,
    ${u.colours.giftDarkGreen},
    ${u.colours.giftLightGreen}
  );
  box-shadow: 0 0 15px ${u.colours.charcoalFaded};
  transition: all 0.3s ease-in;

  &:hover {
    transform: scale(1.01);
    filter: brightness(1.05);
  }
  &:hover .arrow {
    margin: 0 0 0 20px;
  }

  &:hover .link-text::after {
    width: 100%;
  }
`;

S.WorkbookText = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

S.ImageContainer = styled.div`
  width: 100%;
  max-width: 50px;
  position: relative;
  aspect-ratio: 1;
  border-radius: 50%;
  outline: solid 2px ${u.colours.shell};

  @supports not (aspect-ratio: 1) {
    &::before {
      float: left;
      padding-top: 100%;
      content: "";
    }

    &::after {
      display: block;
      content: "";
      clear: both;
    }
  }

  @media (min-width: ${u.device.mobile}) {
  }
`;

S.Overlay = styled.div`
  background: rgba(0, 0, 0, 0.5);
  height: 100%;
  width: 100%;
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
`;

S.Lock = styled(I.Lock)`
  margin-left: 8px;
  margin-bottom: 2px;
`;

S.P = styled.p`
  margin: 0 20px;

  @media ${u.device.desktop} {
    margin: 0;
  }
`;

S.ActivitiesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 16px;
  margin-bottom: 8px;

  @media ${u.device.desktop} {
    padding: 0;
    margin-bottom: 0;
    margin-left: 20px;
  }
`;

S.Activities = styled.div`
  border: 1px solid ${u.colours.shellOverlay};

  @media ${u.device.desktop} {
    border-radius: 5px;
    overflow: hidden;
  }
`;

S.Activity = styled.div`
  align-items: center;
  background: white;
  cursor: pointer;
  display: flex;
  flex: 1;
  justify-content: space-between;
  padding: 10px;
  &:hover {
    background: ${u.colours.pillTealHover};
    *,
    p {
      color: white;
    }
  }
  @media ${u.device.tablet} {
    padding: 10px 10px;
  }
  ${({ active, complete }) => {
    if (active) {
      return `
        background: ${u.colours.pillTealHover};
        *, p {
          color: white;
        }
      `;
    }

    if (complete) {
      return `
        p {
          opacity: 0.5;
        }
      `;
    }
  }}
`;

S.Flex = styled.div`
  align-items: center;
  display: flex;
  flex: 1;
  min-width: 0;
`;

S.FaCheckCircle = styled(FaCheckCircle)`
  color: ${u.colours.pillTealHover};
  margin-right: 10px;
  min-height: 15px;
  min-width: 15px;

  @media ${u.device.tablet} {
    margin-right: 20px;
  }
`;

S.FaPlayCircle = styled(FaPlayCircle)`
  color: rgba(65, 65, 67, 0.6);
  margin-right: 10px;
  min-height: 15px;
  min-width: 15px;

  @media ${u.device.tablet} {
    margin-right: 20px;
  }
`;

S.A = styled.a`
  text-decoration: none;
`;

S.Download = styled(I.Download)`
  margin-right: 10px;
`;

S.IntroWrapper = styled.div`
  margin: 0 20px 16px 16px;

  @media ${u.device.tablet} {
    margin: 0 0 20px 0;
  }
`;

S.MarkDownWrapper = styled.div`
  p {
    font-style: normal;
    font-weight: 500;
    font-size: 1.6rem;
    line-height: 2.2rem;
    color: ${u.colours.charcoalMid};
    margin-bottom: 16px;
  }

  strong {
    display: block;
  }
`;

S.Comments = styled.div`
  margin-bottom: 80px;
  padding: 0 20px;
  @media ${u.device.tablet} {
    padding: 0;
  }
  @media ${u.device.desktop} {
    max-width: 667px;
  }
`;

S.HeroImageContainer = styled.div`
  margin-bottom: 20px;
  width: 100%;
  position: relative;
  aspect-ratio: 22/9;

  @supports not (aspect-ratio: 1) {
    &::before {
      float: left;
      padding-top: 100%;
      content: "";
    }

    &::after {
      display: block;
      content: "";
      clear: both;
    }
  }

  @media (min-width: ${u.device.mobile}) {
  }
`;

S.BgImage = styled(Image)`
  filter: brightness(1.2) blur(8px);
`;

const SI = {};

SI.Container = styled.div`
  padding: 0 20px 0 16px;
  margin: 0;
  margin-top: 20px;

  @media ${u.device.tablet} {
    margin: 20px 0;
    padding: 0 0 20px 0;
    border-bottom: 1px solid ${u.colours.shellOverlay};
  }
`;

SI.TabsWrapper = styled.div`
  padding-bottom: 10px;

  @media ${u.device.tablet} {
    border-bottom: 2px solid ${u.colours.shellOverlay};
    margin-bottom: 20px;
  }
`;

SI.Tabs = styled.div`
  display: flex;
  height: 25px;
`;

SI.TextWrapper = styled.div`
  margin-right: 10px;
`;

SI.A = styled(T.A)`
  color: ${(p) => (p.isActive ? u.colours.charcoal : u.colours.charcoalMid)};
  cursor: pointer;
  font-size: 1.6rem;
  font-weight: 500;
  line-height: 2rem;
  text-transform: none;

  ::after {
    background-color: ${u.colours.charcoalFaded};
  }

  ${(props) => {
    if (props.isActive) {
      return `
      ::after {
        background-color: ${u.colours.charcoal};
        width: 100%;
      }`;
    }
  }}
`;

const TT = {};

TT.CM = styled(T.CM)`
  white-space: nowrap;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-right: 10px;
`;

TT.CXLA = styled(T.CXL)`
  display: none;
  margin: 0 20px 20px 20px;

  @media ${u.device.desktop} {
    margin: 0 0 5px 0;
    display: block;
  }
`;

TT.CXLB = styled(T.CXL)`
  display: block;
  margin-bottom: 10px;

  @media ${u.device.desktop} {
    display: none;
  }
`;

TT.CXXL = styled(T.CXXL)`
  display: none;

  @media ${u.device.tablet} {
    font-size: 2.4rem;
    line-height: 3.6rem;
  }

  @media ${u.device.desktop} {
    display: block;
  }
`;

TT.CLA = styled(T.CL)`
  margin-bottom: 10px;
`;

TT.CLB = styled(T.CL)`
  display: none;

  @media ${u.device.desktop} {
    display: block;
  }
`;

TT.CLC = styled(T.CL)`
  color: white;
  margin-bottom: 10px;
  max-width: 300px;
  text-align: center;
`;

TT.A = styled(T.A)`
  display: block;
  line-height: 30px;
  ::after {
    display: none;
  }
`;

S.LinkText = styled(T.CL)`
  position: relative;
  color: ${u.colours.shell};
  width: fit-content;

  &::after {
    content: "";
    position: absolute;
    width: 0;
    height: 1px;
    background-color: ${u.colours.shell};
    bottom: -5px;
    left: 0;
    transform-origin: left;
    transition: all 0.3s ease-in;
  }
`;

S.Arrow = styled.span`
  display: inline-block;
  width: 0;
  height: 0;
  border-top: 5px solid transparent;
  border-bottom: 5px solid transparent;
  border-left: 9px solid ${u.colours.shell};
  margin-left: 15px;
  transition: all 0.3s ease-in;
`;
